import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { IconButton } from '@mui/material';
import classNames from 'classnames';
import { Ref, useEffect, useState } from 'react';
import css from './DataTable.module.scss';

/**
 * The table's search box. Also rendered outside a table, where one search line serves a page's
 * table and gallery views alike (the library's), so the two look the same.
 */
export function TableSearchField({
  value,
  onChange,
  onBlur,
  placeholder = 'Search media name',
  inputRef,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  inputRef?: Ref<HTMLInputElement>;
  className?: string;
}) {
  return (
    <div className={classNames(css.nameSearch, className)}>
      <SearchRoundedIcon fontSize="small" />
      <span className={css.inputSlot}>
        <input
          ref={inputRef}
          type="search"
          aria-label={placeholder}
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
        />
      </span>
      {value && (
        <IconButton size="small" aria-label="Clear search" onClick={() => onChange('')}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
}

/**
 * The text in a search box whose query a caller owns. The caller's store may trim the query, so
 * this takes the caller's value only when it changed for some other reason (a clear, a view
 * switch). Otherwise a space typed between two terms would vanish.
 */
export function useSearchText(value: string | undefined, onChange?: (value: string) => void) {
  const [text, setText] = useState(value ?? '');
  useEffect(() => {
    if (value !== undefined && value !== text.trim()) setText(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const change = (next: string) => {
    setText(next);
    onChange?.(next);
  };
  return [text, change] as const;
}
